import { keycloak } from '../keycloak';

const REFRESH_SKEW_SECONDS = 90;
const REFRESH_RETRY_MS = 5000;
let refreshTimeoutId;
let watching = false;

// Shared deadline: refresh when the sooner of access/refresh expiry is
// `skew` seconds away. skew is 40% of that token's remaining lifetime at
// issue, floored at 90s. Both the timer and updateToken(minValidity) must
// use this same value — if minValidity is only 40% of remaining time at
// fire, keycloak-js skips the refresh and the timer reschedules at 0ms.
function refreshSkewSeconds() {
  const accessExp = keycloak.tokenParsed?.exp ?? 0;
  const refreshExp = keycloak.refreshTokenParsed?.exp ?? 0;
  const accessIat = keycloak.tokenParsed?.iat;
  const exp = Math.min(accessExp, refreshExp || accessExp);
  const lifetime = Math.max(exp - (accessIat || exp), 0);
  return Math.max(Math.ceil(lifetime * 0.4), REFRESH_SKEW_SECONDS);
}

function minValiditySeconds() {
  const skew = refreshSkewSeconds();
  const accessExp = keycloak.tokenParsed?.exp ?? 0;
  const refreshExp = keycloak.refreshTokenParsed?.exp ?? 0;
  const exp = Math.min(accessExp, refreshExp || accessExp);
  // Access remaining at the shared deadline, so updateToken actually
  // refreshes when the timer fires (including when refresh expires first).
  const remainingAtDeadline = accessExp - (exp - skew);
  return Math.max(Math.ceil(remainingAtDeadline), REFRESH_SKEW_SECONDS);
}

export async function ensureFreshToken() {
  if (!keycloak.authenticated || !keycloak.token) {
    throw new Error('Not authenticated');
  }
  try {
    await keycloak.updateToken(minValiditySeconds());
  } catch (err) {
    await keycloak.login();
    throw err;
  }
}

function refreshThenReschedule() {
  ensureFreshToken()
    .then(scheduleTokenRefresh)
    .catch(() => {
      window.clearTimeout(refreshTimeoutId);
      refreshTimeoutId = window.setTimeout(
        refreshThenReschedule,
        REFRESH_RETRY_MS
      );
    });
}

export function scheduleTokenRefresh() {
  window.clearTimeout(refreshTimeoutId);
  const accessExp = keycloak.tokenParsed?.exp;
  const refreshExp = keycloak.refreshTokenParsed?.exp;
  if (!accessExp || !refreshExp) return;

  const exp = Math.min(accessExp, refreshExp);
  const skew = refreshSkewSeconds();
  // keycloak-js expiry checks use local time + timeSkew; omit it here and a
  // clock-ahead client fires early, updateToken skips, then we reschedule at 0ms.
  const delay = Math.max(
    (exp - skew + (keycloak.timeSkew || 0)) * 1000 - Date.now(),
    0
  );
  refreshTimeoutId = window.setTimeout(refreshThenReschedule, delay);
}

export function startTokenRefreshWatch() {
  scheduleTokenRefresh();
  if (watching) return;
  watching = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    refreshThenReschedule();
  });
}
